import { amlKycChecks, haveValue, isDefaulter } from "./common";

// Application profiles do not necessarily have a top-level tenantid.
// Callers may pass an already filtered selection or explicit tenant IDs.
export const getDefaultCandidacyTenants = (
  profiles = [],
  selectedIds = null
) => {
  const tenants = profiles.filter(Boolean).flatMap((profile) => {
    if (profile.application_id) {
      return (profile.application_id.participants || [])
        .filter(
          (participant) =>
            participant?.consent_status === "accepted" &&
            !participant.is_deleted &&
            !participant.deleted &&
            participant.tenant_info
        )
        .map((participant) => participant.tenant_info);
    }
    const tenant = profile.tenantid ?? profile.tenant_id ?? profile;
    return tenant && typeof tenant === "object" ? [tenant] : [];
  });

  return selectedIds
    ? tenants.filter((tenant) => selectedIds.includes(tenant._id))
    : tenants;
};

export const getCandidacyKycStatus = (tenant) => {
  const kyc = tenant?.onfido_user_kyc;
  if (haveValue(kyc?.kyc_status)) return kyc.kyc_status;
  const workflow = kyc?.onfido_workflow_res;
  return workflow?.object?.status ?? workflow?.status;
};

// This branch's shared checks read Onfido workflow data. Adapt normalized
// KYC records locally so both API formats use those same checks/resolutions.
export const getCandidacyVerificationTenant = (tenant) => {
  const kyc = tenant?.onfido_user_kyc;
  if (!haveValue(kyc?.kyc_status)) return tenant;
  const workflow = kyc.onfido_workflow_res;
  const existingOutput = workflow?.output ?? workflow?.resource?.output;
  if (existingOutput) {
    // Use the same category keys the profile screen displays and resolves.
    // A status-only migration must not replace that report with empty checks.
    return {
      ...tenant,
      onfido_user_kyc: {
        ...kyc,
        onfido_workflow_res: {
          ...workflow,
          object: { ...workflow?.object, status: kyc.kyc_status },
          status: kyc.kyc_status,
        },
      },
    };
  }
  const amlCategoryKeys = {
    sanction: "sanction",
    pep: "politically_exposed_person",
    adverse_media: "adverse_media",
    warnings: "legal_and_regulatory_warnings",
  };
  const documentBreakdown = Object.fromEntries(
    Object.entries(kyc.kyc_checks?.categories ?? {}).map(([key, result]) => [
      key,
      { result },
    ])
  );
  const amlBreakdown = Object.fromEntries(
    Object.entries(amlCategoryKeys)
      .filter(([field]) => typeof kyc.kyc_aml?.[field] === "string")
      .map(([field, key]) => [key, { result: kyc.kyc_aml[field] }])
  );
  return {
    ...tenant,
    onfido_user_kyc: {
      ...kyc,
      onfido_workflow_res: {
        status: kyc.kyc_status,
        output: {
          document_report: { breakdown: documentBreakdown },
          aml_report: { result: kyc.kyc_aml?.result, breakdown: amlBreakdown },
        },
      },
    },
  };
};

export const needsCandidacyVerification = (tenant, resolutions) => {
  const status = getCandidacyKycStatus(tenant);
  return (
    haveValue(status) &&
    status !== "not_started" &&
    !amlKycChecks(getCandidacyVerificationTenant(tenant), resolutions)
  );
};

export const getDefaultCandidacyChecks = (
  tenants,
  resolutionsById = {},
  financials = null
) => {
  const defaulters = tenants.filter((tenant) =>
    isDefaulter(
      tenant?.tenant_extra_field_id?.experianJson?.["s:Envelope"]?.["s:Body"]
        ?.GenerarInformeResponse?.GenerarInformeResult?.InformeDelphi4A3
    )
  );
  const pendingVerification = tenants.filter((tenant) =>
    needsCandidacyVerification(tenant, resolutionsById[tenant._id])
  );
  const effortRate = financials?.effortRate ?? null;
  const effortRateExceeded = typeof effortRate === "number" && effortRate > 50;
  const status =
    tenants.length === 0
      ? "unselected"
      : defaulters.length > 0 || effortRateExceeded
        ? "declined"
        : pendingVerification.length > 0
          ? "review"
          : "approved";
  const titleKey = {
    unselected: "selectTenantsToCheckApproval",
    declined: "notApproved",
    review: "manualReview",
    approved: "approved",
  }[status];
  return {
    status,
    titleKey,
    defaulters,
    pendingVerification,
    effortRateExceeded,
    effortRate,
    totalIncome: financials?.totalIncome ?? null,
  };
};
